import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem } from 'lucide-react'
import audioService from '../../../services/audioService'

const RoyaleBetsFeed = ({ bets = [], showTicketRange = false }) => {
  const { t } = useTranslation()
  const prevBetIdsRef = useRef(null)

  useEffect(() => {
    const currentIds = new Set((bets || []).map((bet) => String(bet.id)))

    if (!prevBetIdsRef.current) {
      prevBetIdsRef.current = currentIds
      return
    }

    const hasNewBet = Array.from(currentIds).some((id) => !prevBetIdsRef.current.has(id))
    if (hasNewBet) {
      audioService.play('userJoinBattle')
    }

    prevBetIdsRef.current = currentIds
  }, [bets])
  if (!bets || bets.length === 0) {
    return (
      <div></div>
    )
  }

  return (
    <div className="">
      <h2 className="text-white font-semibold mb-3">{t('royale.betsFeed.title')}</h2>
      <div className="space-y-3">
        {bets.map((bet) => (
          <motion.div
            key={bet.id}
            initial="enter"
            animate="center"
            variants={{
              enter: { opacity: 0, rotateY: -90, scale: 0.5 },
              center: {
                opacity: 1,
                rotateY: 0,
                scale: 1,
                transition: {
                  duration: 0.65,
                  ease: [0.34, 1.56, 0.64, 1],
                  opacity: { duration: 0.2 }
                }
              }
            }}
            style={{
              transformStyle: 'preserve-3d',
              perspective: '1000px'
            }}
            className={`rounded-lg p-3 border transition-colors ${
              bet.win_bet
                ? 'border-accent-primary/60 bg-accent-primary/10'
                : 'border-white/10 bg-dark-primary/60'
            } ${
              bet.your_bet
                ? 'ring-1 ring-accent-secondary/70'
                : ''
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <img
                  src={bet.ava}
                  alt={bet.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">
                    {String(bet.name || '')?.length > 20
                      ? `${String(bet.name).slice(0, 20)}…`
                      : bet.name}
                  </p>
                  {showTicketRange && (bet.ticket_start || bet.ticket_finish) && (
                    <p className="text-[11px] text-text-secondary">
                      {Number(bet.ticket_start || 0).toLocaleString()} – {Number(bet.ticket_finish || 0).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-accent-primary text-md inline-flex items-center gap-1 shrink-0 ml-auto text-right">
                {Number(bet.total_crystal || 0).toLocaleString()}
                <Gem className="w-3.5 h-3.5 text-accent-primary" />
              </span>
            </div>

            {(bet.skins && bet.skins.length > 0) || Number(bet.crystal || 0) > 0 ? (
              <div className="flex gap-2 flex-wrap mt-4">
                {Number(bet.crystal || 0) > 0 && (
                  <div className="w-16 h-16 rounded-lg bg-dark-primary/80 border border-accent-primary/40 flex flex-col items-center justify-center gap-2">
                    <Gem className="w-4 h-4 text-accent-primary" />
                    <span className="text-[10px] font-semibold text-white leading-none">
                      {Number(bet.crystal || 0).toLocaleString()}
                    </span>
                  </div>
                )}
                {(bet.skins || []).map((skin) => (
                  <div
                    key={skin.id}
                    className="w-16 h-16 rounded-lg bg-dark-primary/80 border"
                    style={{ borderColor: `#${skin.color}` }}
                  >
                    <img
                      src={skin.img}
                      alt={skin.name}
                      className="w-full h-full object-contain p-2"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default RoyaleBetsFeed
